import { useState } from "react";
import { CalendarClock, FolderKanban } from "lucide-react";
import { fmtEur } from "@/lib/format";
import { STAGES, partnerName, todayIso } from "./crmConst";

const ClientCard = ({ c, partners, onOpen, dragging, onDragStart, onDragEnd }) => (
  <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onOpen}
    className={`rounded-lg border border-border bg-card p-3 cursor-grab active:cursor-grabbing shadow-sm transition-[opacity,box-shadow,border-color] hover:border-genis/60 hover:shadow ${dragging ? "opacity-40" : ""}`}
    data-testid={`crm-card-${c.id}`}>
    <div className="font-semibold text-sm leading-tight">{c.company}</div>
    {c.contact_name && <div className="text-xs text-muted-foreground mt-0.5">{c.contact_name}</div>}
    <div className="flex items-center justify-between mt-2 text-xs">
      <span className="text-muted-foreground truncate max-w-[60%]">{partnerName(partners, c.partner_id)}</span>
      <span className="metric font-bold text-genis">{fmtEur(c.total_prime)}</span>
    </div>
    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
      <span className="flex items-center gap-1"><FolderKanban className="h-3 w-3" /> {c.project_count} projet(s)</span>
      {c.next_task && (
        <span className={`flex items-center gap-1 ${c.next_task.due_date < todayIso() ? "text-red-600 font-semibold" : ""}`}>
          <CalendarClock className="h-3 w-3" /> {c.next_task.due_date.split("-").reverse().join("/")}
        </span>
      )}
    </div>
  </div>
);

export const Pipeline = ({ clients, partners, onMove, onOpen }) => {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  return (
    <div className="grid grid-flow-col auto-cols-[minmax(210px,1fr)] xl:grid-flow-row xl:grid-cols-6 gap-2.5 overflow-x-auto pb-2" data-testid="crm-pipeline">
      {STAGES.map((s) => {
        const items = clients.filter((c) => c.stage === s.v);
        const total = items.reduce((a, c) => a + (c.total_prime || 0), 0);
        return (
          <div key={s.v}
            onDragOver={(e) => { e.preventDefault(); setOver(s.v); }}
            onDragLeave={() => setOver((o) => (o === s.v ? null : o))}
            onDrop={(e) => { e.preventDefault(); setOver(null); if (dragId) onMove(dragId, s.v); setDragId(null); }}
            className={`rounded-xl bg-secondary/50 p-2.5 min-h-[320px] transition-[box-shadow,background-color] ${over === s.v ? "ring-2 ring-genis bg-genis/5" : ""}`}
            data-testid={`crm-col-${s.v}`}>
            <div className="px-1 pb-2.5">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span className={`h-2 w-2 rounded-full ${s.dot}`} /> {s.label}
                <span className="ml-auto text-xs text-muted-foreground font-normal">{items.length}</span>
              </div>
              <div className="metric text-xs text-muted-foreground mt-0.5" data-testid={`crm-col-total-${s.v}`}>{fmtEur(total)}</div>
            </div>
            <div className="space-y-2">
              {items.map((c) => (
                <ClientCard key={c.id} c={c} partners={partners} dragging={dragId === c.id} onOpen={() => onOpen(c.id)}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragId(c.id); }} onDragEnd={() => { setDragId(null); setOver(null); }} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
